import React, { useState } from 'react';
import { apiFetchAuthed } from '../../apiHelpers';
import useChatStore from '../../stores/useChatStore';

/**
 * One quiet line under an answer: was it helpful? A no opens four reasons and
 * a note. One verdict per person per answer; the server replaces an earlier one.
 */

export const REASONS: { key: string; label: string }[] = [
  { key: 'wrong_number', label: 'Wrong number' },
  { key: 'missed', label: 'Did not answer my question' },
  { key: 'tone', label: 'Tone was off' },
  { key: 'other', label: 'Something else' },
];

export type Feedback = { verdict: 'up' | 'down'; reason?: string | null; note?: string | null } | null;

type Props = { messageId: string; feedback?: Feedback };

const linkStyle: React.CSSProperties = { color: '#6b6b6b', fontSize: 10, background: 'none', border: 0, padding: 0, cursor: 'pointer' };

const FlerpieFeedback: React.FC<Props> = ({ messageId, feedback = null }) => {
  const [stage, setStage] = useState<'ask' | 'why' | 'done'>(feedback ? 'done' : 'ask');
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (verdict: 'up' | 'down', r: string | null, n: string) => {
    setBusy(true);
    try {
      await apiFetchAuthed(`/ai/messages/${messageId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ verdict, reason: r, note: n }),
      });
      useChatStore.getState().setMessageFeedback(messageId, { verdict, reason: r, note: n });
      setStage('done');
    } catch (err) {
      console.error('Feedback failed:', err);
    } finally {
      setBusy(false);
    }
  };

  if (stage === 'done') {
    return <div className="mt-1 ml-1 text-[10px] text-gray-600">Thanks, noted.</div>;
  }

  if (stage === 'why') {
    return (
      <div className="mt-1 ml-1 flex flex-col gap-1.5">
        <div className="flex flex-wrap gap-1.5">
          {REASONS.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setReason(r.key)}
              aria-pressed={reason === r.key}
              className="text-[10px] px-2 py-0.5 rounded-full transition-colors"
              style={{
                border: '1px solid rgba(255,204,51,0.18)',
                color: reason === r.key ? '#121212' : '#bfa64a',
                background: reason === r.key ? '#ffcc33' : 'transparent',
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1.5 items-center">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What should it have said?"
            aria-label="What should it have said"
            className="flex-1 min-w-0 bg-transparent text-[11px] text-gray-300 placeholder:text-gray-600 rounded-md px-2 py-1 m-0"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
            maxLength={500}
          />
          <button
            type="button"
            disabled={busy || (!reason && !note.trim())}
            onClick={() => send('down', reason, note)}
            className="text-[10px] px-2 py-1 rounded-md disabled:opacity-40"
            style={{ background: 'rgba(255,204,51,0.15)', color: '#ffcc33' }}
          >
            Send
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-1 ml-1 flex items-center gap-2 text-[10px] text-gray-600">
      <span>Helpful?</span>
      <button type="button" style={linkStyle} className="hover:text-gray-400" aria-label="Yes, helpful" disabled={busy} onClick={() => send('up', null, '')}>Yes</button>
      <button type="button" style={linkStyle} className="hover:text-gray-400" aria-label="No, not helpful" disabled={busy} onClick={() => setStage('why')}>No</button>
    </div>
  );
};

export default FlerpieFeedback;
